import type { Order } from '@shared/types';

interface Props {
  orders: Order[];
}

interface FailureRow {
  reason: string;
  count: number;
  percentage: number;
}

export default function FailureAnalysis({ orders }: Props) {
  const failed = orders.filter((o) => o.status === 'failed' || o.status === 'expired');
  if (failed.length === 0) return null;

  const reasons: Record<string, number> = {};
  for (const o of failed) {
    const reason = o.status === 'expired'
      ? 'Payment Expired'
      : `Failed — ${o.provider || o.payment_provider || 'unknown'}`;
    reasons[reason] = (reasons[reason] || 0) + 1;
  }

  const rows: FailureRow[] = Object.entries(reasons)
    .sort(([, a], [, b]) => b - a)
    .map(([reason, count]) => ({
      reason,
      count,
      percentage: (count / failed.length) * 100,
    }));

  return (
    <div
      className="rounded p-5"
      style={{
        border: '1px solid var(--dash-border)',
        background: 'var(--dash-card)',
      }}
    >
      <div
        className="font-mono text-[10px] uppercase tracking-widest mb-1"
        style={{ color: 'var(--dash-text-dim)' }}
      >
        Failure Analysis
      </div>
      <div
        className="font-mono text-[10px] mb-4"
        style={{ color: 'var(--dash-text-faint)' }}
      >
        {failed.length} failed/expired order{failed.length !== 1 ? 's' : ''}
      </div>
      <div className="space-y-3">
        {rows.map((row) => (
          <div key={row.reason}>
            <div className="flex items-center justify-between mb-1">
              <span
                className="font-sans text-xs"
                style={{ color: 'var(--dash-text-muted)' }}
              >
                {row.reason}
              </span>
              <span
                className="font-mono text-xs"
                style={{ color: 'var(--dash-text-dim)' }}
              >
                {row.count} ({row.percentage.toFixed(0)}%)
              </span>
            </div>
            <div
              className="h-1.5 rounded overflow-hidden"
              style={{ background: 'var(--dash-hover-bg)' }}
            >
              <div
                className="h-full bg-red-500/40 rounded transition-all duration-300"
                style={{ width: `${row.percentage}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
